import { useEffect, useState } from 'react'
import { ScoopSpinner, ScoopButton, ScoopText, ScoopChip } from '../ui'
import { useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { exchangeAppSumoCode, linkAppSumoLicense } from '../../utils/appsumo/AppSumoManager'
import { ROUTES } from '../../router/routes'
import { Server } from '../../api/Server'
import { CheckCircleIcon, ArrowRightIcon } from '@phosphor-icons/react'
import ScoopLogo from '../../assets/common/ScoopLogo.svg'
import styles from './AppSumoAuthFlow.module.scss'

export const AppSumoAuthFlow = ({ code }) => {
    const navigate = useNavigate()
    const isAuthenticated = useSelector((state) => state.auth.isAuthenticated)
    const userID = useSelector((state) => state.auth.userID)
    const token = useSelector((state) => state.auth.token)
    const workspaceID = useSelector((state) => state.auth.workspaceID)
    const [status, setStatus] = useState('processing')
    const [message, setMessage] = useState('')
    const [licenseData, setLicenseData] = useState(null)
    const [isLinking, setIsLinking] = useState(false)
    const [hasExchanged, setHasExchanged] = useState(false)

    useEffect(() => {
        // Only exchange once when component mounts
        if (hasExchanged) return

        const doExchange = async () => {
            const server = new Server(null, null, null)
            const result = await exchangeAppSumoCode(code, server)
            setHasExchanged(true)

            if (!result.success) {
                setStatus('error')
                setMessage('Something went wrong. Please try again or contact support.')
                return
            }

            setLicenseData(result.licenseData)

            // Check if already claimed by current user
            if (result.isAlreadyClaimed) {
                setStatus('alreadyClaimed')
                setMessage('Your AppSumo license is already active')
                setTimeout(() => navigate(ROUTES.ACCOUNT), 2000)
                return
            }

            // Show appropriate UI based on auth status
            if (!isAuthenticated) {
                setStatus('needsAuth')
            } else {
                setStatus('readyToLink')
            }
        }
        doExchange()
    }, [code, hasExchanged])

    // Separate effect to handle auth status changes
    useEffect(() => {
        // Only update status if we've already exchanged and status is needsAuth
        if (hasExchanged && status === 'needsAuth' && isAuthenticated) {
            setStatus('readyToLink')
        }
    }, [isAuthenticated, hasExchanged, status])

    const handleLinkLicense = async () => {
        setIsLinking(true)
        const server = new Server(workspaceID, userID, token)
        const result = await linkAppSumoLicense(licenseData?.licenseKey, server)

        if (result.success) {
            setStatus('success')
            setMessage(result.message)
            setTimeout(() => navigate(ROUTES.ACCOUNT), 2000)
        } else {
            setStatus('error')
            setMessage('Something went wrong. Please try again or contact support.')
        }
        setIsLinking(false)
    }

    if (status === 'processing') {
        return (
            <div className={styles.container}>
                <div className={styles.logoRow}>
                    <div className={styles.appSumoBadge}>
                        <span className={styles.appSumoBadgeText}>AS</span>
                    </div>
                    <ArrowRightIcon size={32} className={styles.arrowIcon} />
                    <img src={ScoopLogo} alt="Scoop" className={styles.logo} />
                </div>
                <ScoopSpinner size={40} />
                <ScoopText className={styles.loadingText}>
                    Verifying your AppSumo license...
                </ScoopText>
            </div>
        )
    }

    // Show create account UI for non-authenticated users
    if (status === 'needsAuth') {
        return (
            <div className={styles.container}>
                <img
                    src={ScoopLogo}
                    alt="Scoop Logo"
                    style={{ width: '150px', height: '150px', marginBottom: '20px' }}
                />

                <ScoopText className={styles.title}>Welcome to Scoop Analytics!</ScoopText>

                <ScoopChip
                    label={`${licenseData?.displayName || 'AppSumo'} Lifetime Deal`}
                    className={styles.chip}
                />

                <div className={styles.card}>
                    <ScoopText className={styles.cardText}>
                        Your AppSumo license has been verified successfully! Create your account to
                        start analyzing your data with powerful insights.
                    </ScoopText>

                    <div className={styles.featureList}>
                        <div className={styles.featureItem}>
                            <CheckCircleIcon
                                size={24}
                                weight="fill"
                                className={styles.featureIcon}
                            />
                            <ScoopText className={styles.featureText}>
                                Lifetime access to all features
                            </ScoopText>
                        </div>
                        <div className={styles.featureItem}>
                            <CheckCircleIcon
                                size={24}
                                weight="fill"
                                className={styles.featureIcon}
                            />
                            <ScoopText className={styles.featureText}>
                                Connect unlimited data sources
                            </ScoopText>
                        </div>
                        <div className={styles.featureItem}>
                            <CheckCircleIcon
                                size={24}
                                weight="fill"
                                className={styles.featureIcon}
                            />
                            <ScoopText className={styles.featureText}>
                                AI-powered analytics & insights
                            </ScoopText>
                        </div>
                    </div>
                </div>

                <ScoopButton
                    className={styles.button}
                    onClick={() =>
                        navigate(ROUTES.LOGIN, {
                            state: {
                                appsumoLicense: licenseData?.licenseKey,
                                appsumoTier: licenseData?.tier,
                                appsumoDisplayName: licenseData?.displayName,
                                fromAppSumoOAuth: true,
                                showSignup: true,
                            },
                        })
                    }
                >
                    Sign Up
                </ScoopButton>

                <ScoopText className={styles.disclaimer}>
                    By creating an account, you agree to our Terms of Service and Privacy Policy
                </ScoopText>
            </div>
        )
    }

    // Show link button for authenticated users
    if (status === 'readyToLink') {
        return (
            <div className={styles.container}>
                <img
                    src={ScoopLogo}
                    alt="Scoop Logo"
                    style={{ width: '150px', height: '150px', marginBottom: '20px' }}
                />

                <ScoopText className={styles.title}>Almost There!</ScoopText>

                <div className={`${styles.card} ${styles.cardSmall}`}>
                    <span className={styles.badge}>
                        {licenseData?.displayName || 'AppSumo'} Plan
                    </span>

                    <ScoopText className={`${styles.cardText} ${styles.cardTextNoMargin}`}>
                        Your AppSumo license has been verified. Click below to activate your
                        lifetime access and unlock all premium features.
                    </ScoopText>
                </div>

                <ScoopButton
                    className={`${styles.button} ${styles.buttonWide}`}
                    onClick={handleLinkLicense}
                    disabled={isLinking}
                >
                    {isLinking ? (
                        <ScoopSpinner size={20} />
                    ) : (
                        'Connect your AppSumo license to Scoop User now'
                    )}
                </ScoopButton>
            </div>
        )
    }

    // Show already claimed message
    if (status === 'alreadyClaimed') {
        return (
            <div className={styles.container}>
                <CheckCircleIcon
                    size={80}
                    weight="fill"
                    className={styles.featureIcon}
                    style={{ marginBottom: '24px' }}
                />
                <ScoopText className={styles.title}>License Already Active!</ScoopText>
                <ScoopText className={styles.cardText} style={{ marginBottom: '24px' }}>
                    {message}
                </ScoopText>
                <div className={styles.redirectRow}>
                    <ScoopSpinner size={20} />
                    <ScoopText className={styles.redirectText}>
                        Redirecting to your account...
                    </ScoopText>
                </div>
            </div>
        )
    }

    // Default state for success/error
    return (
        <div className={styles.container}>
            <img
                src={ScoopLogo}
                alt="Scoop Logo"
                style={{ width: '150px', height: '150px', marginBottom: '20px' }}
            />

            <ScoopText className={styles.title} style={{ marginBottom: '24px' }}>
                {status === 'success' ? 'Successfully Connected!' : 'Connection Failed'}
            </ScoopText>

            <div className={`${styles.card} ${styles.cardSmall}`}>
                <ScoopText className={styles.cardText} style={{ marginBottom: 0 }}>
                    {status === 'error'
                        ? 'Something went wrong. Please try again or contact support.'
                        : message}
                </ScoopText>
            </div>

            {status === 'success' && (
                <div className={styles.redirectRow}>
                    <ScoopSpinner size={20} />
                    <ScoopText className={styles.redirectText}>
                        Redirecting to your account...
                    </ScoopText>
                </div>
            )}

            {status === 'error' && (
                <ScoopButton
                    onClick={() => window.location.reload()}
                    style={{ width: '200px', fontWeight: 'bold' }}
                >
                    Try Again
                </ScoopButton>
            )}
        </div>
    )
}
